import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, BookMarked, Wand2, Globe, Layers, GraduationCap, FileText, School, Image as ImageIcon, UploadCloud, X, CheckCircle2, FileType2, ShieldCheck, Youtube, Link as LinkIcon, Search } from "lucide-react";
import { getGenerateConfig, listClassrooms, uploadExtract, startGenerationJob, getUploadLimits, ingestYouTube, searchYouTube } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StandardsBadges } from "@/components/StandardsBadges";
import { ContentVolumePicker } from "@/components/ContentVolumePicker";
import { toast } from "sonner";

const LEVELS = [
  { value: "high school", label: "High School" },
  { value: "undergraduate", label: "Undergraduate" },
  { value: "graduate", label: "Graduate" },
  { value: "expert", label: "Expert / Deep Dive" },
];

const DEPTH_DEFAULTS = [
  { id: "quick",         label: "Quick",         blurb: "Skim — the essentials",              slides: "6-8",   quiz: 8,   time: "~30s" },
  { id: "standard",      label: "Standard",      blurb: "Balanced study session",             slides: "14-18", quiz: 25,  time: "~1 min" },
  { id: "deep",          label: "Deep",          blurb: "Thorough — real exam prep",          slides: "28-32", quiz: 50,  time: "~2 min" },
  { id: "certification", label: "Certification", blurb: "Full mastery — 45-slide deck, 100 Qs", slides: "45-50", quiz: 100, time: "~3-4 min" },
];

// English default + starter set + widely-spoken languages in the US and worldwide
const LANGUAGES = [
  "English",
  "Spanish (Español)",
  "Chinese — Simplified (简体中文)",
  "Hindi (हिन्दी)",
  "Arabic (العربية)",
  "French (Français)",
  "Portuguese (Português)",
  "Russian (Русский)",
  "German (Deutsch)",
  "Italian (Italiano)",
  "Japanese (日本語)",
  "Korean (한국어)",
  "Bengali (বাংলা)",
  "Indonesian (Bahasa Indonesia)",
  "Tagalog / Filipino",
  "Vietnamese (Tiếng Việt)",
  "Urdu (اردو)",
  "Persian / Farsi (فارسی)",
  "Turkish (Türkçe)",
  "Polish (Polski)",
  "Ukrainian (Українська)",
  "Dutch (Nederlands)",
  "Haitian Creole (Kreyòl Ayisyen)",
  "Greek (Ελληνικά)",
  "Hebrew (עברית)",
  "Thai (ไทย)",
  "Swahili (Kiswahili)",
];

const HERO =
  "https://images.unsplash.com/photo-1741699427768-fbb97fc21425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzdHVkeSUyMGJvb2tzJTIwdW5pdmVyc2l0eSUyMGxpYnJhcnklMjBjb3p5JTIwcmVhZGluZyUyMGRlc2t8ZW58MHx8fHwxNzg4M105OEB0&ixlib=rb-4.1.0&q=85";

const SOURCE_TEXT_MAX_CHARS = 80000;

export const GeneratorForm = ({ onGenerate, loading, progress }) => {
  const { user } = useAuth();
  const [book, setBook] = useState("");
  const [chapters, setChapters] = useState("");
  const [scope, setScope] = useState("chapter");
  const [chapterMode, setChapterMode] = useState("number");
  const [level, setLevel] = useState("undergraduate");
  const [edition, setEdition] = useState("");
  const [depth, setDepth] = useState(() => localStorage.getItem("lectura_depth") || "standard");
  const [depths, setDepths] = useState(DEPTH_DEFAULTS);
  const [language, setLanguage] = useState(() => localStorage.getItem("lectura_language") || "English");
  const [sourceText, setSourceText] = useState("");
  const [includeImages, setIncludeImages] = useState(() => localStorage.getItem("lectura_include_images") === "1");
  // Content Volume Picker — 13 options. Default stays as today's basics so free users don't get surprised.
  const DEFAULT_COMPONENTS = ["slides", "study_guide", "vocabulary", "mc_quiz", "flashcards"];
  const [components, setComponents] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("lectura_components") || "null");
      return Array.isArray(saved) && saved.length ? saved : DEFAULT_COMPONENTS;
    } catch { return DEFAULT_COMPONENTS; }
  });
  const toggleComponent = (id) => {
    setComponents((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
      try { localStorage.setItem("lectura_components", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [classroomId, setClassroomId] = useState("");
  const [chapterLabel, setChapterLabel] = useState("");
  const [myClasses, setMyClasses] = useState([]);
  const [uploads, setUploads] = useState([]); // [{id, file, filename, kind, text, chars, truncated, titleOverride, chapterOverride, status, error}]
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [uploadLimits, setUploadLimits] = useState(null); // {used, limit, remaining}
  // YouTube ingest state
  const [ytUrl, setYtUrl] = useState("");
  const [ytFetching, setYtFetching] = useState(false);
  const [ytResult, setYtResult] = useState(null); // {text, chars, video_id, thumbnail, duration_seconds, language}
  const [ytMode, setYtMode] = useState("url");   // "url" | "search"
  const [ytQuery, setYtQuery] = useState("");
  const [ytSearching, setYtSearching] = useState(false);
  const [ytSearchResults, setYtSearchResults] = useState(null); // null | Array
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const isInstructor = user && (user.role === "instructor" || user.role === "admin");

  useEffect(() => {
    getGenerateConfig()
      .then((cfg) => {
        if (!cfg?.depths?.length) return;
        // Merge server counts into our labels (server is source of truth for counts)
        const merged = DEPTH_DEFAULTS.map((d) => {
          const s = cfg.depths.find((x) => x.id === d.id) || {};
          return { ...d, slides: s.slides || d.slides, quiz: s.quiz ?? d.quiz };
        });
        setDepths(merged);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isInstructor) return;
    listClassrooms().then((rows) => setMyClasses(rows || [])).catch(() => setMyClasses([]));
  }, [isInstructor]);

  // Poll upload-limits when the user enters upload scope so the "X of 20 this hour"
  // counter is accurate. We refresh on completed upload too (see addFiles).
  const refreshLimits = () => getUploadLimits().then(setUploadLimits).catch(() => {});
  useEffect(() => {
    if (scope === "upload") refreshLimits();
  }, [scope]);

  // If the user clicked "Upload files" inside a specific Class Library, we stashed
  // the target class + chapter in sessionStorage. Restore it here on mount so the
  // Create form opens ready-to-go.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("lectura_pending_upload");
      if (!raw) return;
      const pending = JSON.parse(raw);
      sessionStorage.removeItem("lectura_pending_upload");
      if (pending?.classroom_id) {
        setScope("upload");
        setClassroomId(pending.classroom_id);
        if (pending.chapter_label) setChapterLabel(pending.chapter_label);
        toast.message(`Uploading into ${pending.class_name || "your class"} — files become lectures here.`);
      }
    } catch {}
  }, []);

  // Auto-fill chapter label when instructor picks a class (or scope/chapters change), so libraries stay organized.
  useEffect(() => {
    if (!classroomId) return;
    if (chapterLabel) return;
    if (scope === "chapter") {
      const c = chapters.trim();
      if (c) {
        setChapterLabel(c.match(/^\d+([-\s,]+\d+)*$/) ? `Chapter ${c}` : c);
      } else {
        setChapterLabel("Chapter");
      }
    } else if (scope === "book") {
      setChapterLabel("Whole book");
    } else if (scope === "paste") {
      setChapterLabel("Pasted source");
    }
  }, [classroomId, chapters, scope, chapterLabel]);

  const changeDepth = (val) => {
    setDepth(val);
    localStorage.setItem("lectura_depth", val);
  };

  // ---------- File upload handlers ----------
  const ACCEPTED = ".pdf,.docx,.doc,.pptx,.ppt,.png,.jpg,.jpeg,.webp,.txt,.md";

  const addFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    // Cap batch size to avoid firing dozens of parallel LLM jobs by accident
    const remaining = Math.max(0, 10 - uploads.length);
    if (arr.length > remaining) {
      toast.warning(`Batch capped at 10 files — extra ${arr.length - remaining} skipped.`);
    }
    const toAdd = arr.slice(0, remaining).map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      filename: f.name,
      status: "extracting",
    }));
    if (!toAdd.length) return;
    setUploads((u) => [...u, ...toAdd]);
    for (const entry of toAdd) {
      // Extract sequentially so a huge PDF doesn't starve the browser
      try {
        const r = await uploadExtract(entry.file);
        setUploads((u) => u.map((x) => x.id === entry.id ? {
          ...x,
          status: "ready",
          filename: r.filename,
          kind: r.kind,
          text: r.text,
          chars: r.chars,
          truncated: r.truncated,
          titleOverride: r.suggested_title,
        } : x));
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Could not read this file.";
        setUploads((u) => u.map((x) => x.id === entry.id ? { ...x, status: "error", error: msg } : x));
        // 429 → surface it prominently and stop the batch so the user
        // doesn't watch every remaining file fail with the same message
        if (e?.response?.status === 429) {
          toast.error(msg);
          refreshLimits();
          break;
        }
      }
      refreshLimits();
    }
  };

  const removeUpload = (id) => setUploads((u) => u.filter((x) => x.id !== id));

  const onDrop = (e) => {
    e.preventDefault();
    dropZoneRef.current?.classList.remove("border-accent", "bg-accent/5");
    addFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dropZoneRef.current?.classList.add("border-accent", "bg-accent/5");
  };
  const onDragLeave = () => {
    dropZoneRef.current?.classList.remove("border-accent", "bg-accent/5");
  };

  const submitUploads = async () => {
    const ready = uploads.filter((u) => u.status === "ready" && u.text);
    if (!ready.length) {
      toast.error("Add at least one file before generating.");
      return;
    }
    setBatchGenerating(true);
    try {
      const jobs = await Promise.all(
        ready.map((u) =>
          startGenerationJob({
            book_title: (u.titleOverride || u.filename).trim() || "Chapter",
            chapters: "",
            level,
            language,
            edition: edition.trim(),
            depth,
            source_text: u.text.slice(0, SOURCE_TEXT_MAX_CHARS),
            include_images: includeImages,
            classroom_id: classroomId || "",
            chapter_label: classroomId ? (u.chapterOverride?.trim() || chapterLabel.trim() || "Uploaded") : "",
            components,
          })
        )
      );
      toast.success(`Started ${jobs.length} ${jobs.length === 1 ? "lecture" : "lectures"} — they'll appear in your library as they finish.`);
      setUploads([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the batch. Please try again.");
    } finally {
      setBatchGenerating(false);
    }
  };

  const needsChapter = scope === "chapter";
  const needsPastedText = scope === "paste";
  const isYoutube = scope === "youtube";
  const canSubmit =
    (isYoutube ? !!ytResult?.text : book.trim()) &&
    (!needsChapter || chapters.trim()) &&
    (!needsPastedText || sourceText.trim().length >= 400) &&
    !loading &&
    !ytFetching;
  // Human-readable reason the button is disabled — turns a dead button into a nudge.
  const disabledReason = (() => {
    if (loading) return "";
    if (ytFetching) return "Fetching captions… hang on";
    if (isYoutube && !ytResult?.text) return "Paste a YouTube URL and hit \"Pull captions\" first";
    if (!isYoutube && !book.trim()) return "Enter a book title to get started";
    if (needsChapter && !chapters.trim()) return "Add a chapter number or range (e.g. \"5\" or \"5-7\")";
    if (needsPastedText && sourceText.trim().length < 400) return `Paste at least 400 characters (${sourceText.trim().length}/400)`;
    return "";
  })();

  const changeLanguage = (val) => {
    setLanguage(val);
    localStorage.setItem("lectura_language", val);
  };

  const fetchYoutube = async (overrideUrl = null) => {
    const targetUrl = overrideUrl ?? ytUrl.trim();
    if (!targetUrl || ytFetching) return;
    setYtFetching(true);
    setYtResult(null);
    try {
      const data = await ingestYouTube(targetUrl, language.toLowerCase().slice(0, 2) || "en");
      setYtResult(data);
      // Auto-fill title/chapters so the generated lecture has clean metadata even
      // if the user leaves those fields blank.
      if (!book.trim()) setBook(`YouTube · ${data.video_id}`);
      if (!chapters.trim()) setChapters(`youtube.com/watch?v=${data.video_id}`);
      toast.success(`Pulled ${data.chars.toLocaleString()} chars from the video`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch that video");
    } finally {
      setYtFetching(false);
    }
  };

  const runYoutubeSearch = async (e) => {
    e?.preventDefault?.();
    const q = ytQuery.trim();
    if (q.length < 2 || ytSearching) return;
    setYtSearching(true);
    setYtSearchResults(null);
    try {
      const data = await searchYouTube(q, 8);
      setYtSearchResults(data.results || []);
      if (!data.results?.length) toast.info("No videos matched that search. Try different words.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed — try again.");
      setYtSearchResults([]);
    } finally {
      setYtSearching(false);
    }
  };

  const pickSearchResult = (r) => {
    // Move to URL tab and prefill so the "Transcript ready" preview shows.
    setYtMode("url");
    setYtUrl(r.url);
    // Prefill title/chapters with the actual video title from the search — much
    // nicer than the "YouTube · <id>" fallback ingest sets.
    if (!book.trim()) setBook(r.title);
    if (!chapters.trim()) setChapters(r.channel ? `${r.channel} · ${r.duration_text || "video"}` : r.url);
    fetchYoutube(r.url);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    localStorage.setItem("lectura_language", language);
    localStorage.setItem("lectura_include_images", includeImages ? "1" : "0");
    onGenerate({
      book_title: isYoutube ? (book.trim() || `YouTube · ${ytResult?.video_id}`) : book.trim(),
      chapters: scope === "book" ? "" : (isYoutube ? (chapters.trim() || `youtube.com/watch?v=${ytResult?.video_id}`) : chapters.trim()),
      level,
      language,
      edition: edition.trim(),
      depth,
      source_text: needsPastedText
        ? sourceText.trim().slice(0, SOURCE_TEXT_MAX_CHARS)
        : (isYoutube ? (ytResult?.text || "").slice(0, SOURCE_TEXT_MAX_CHARS) : ""),
      include_images: includeImages,
      classroom_id: classroomId || "",
      chapter_label: classroomId ? chapterLabel.trim() : "",
      components,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-5">
            <BookMarked className="h-4 w-4" /> Turn any chapter into a lecture
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Name a book.<br />Get a lecture,<br />
            <span className="text-accent italic">study guide</span> & quiz.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
            Lectura synthesizes a narrated lecture, presentation slides, a printable study
            guide, and a fun graded quiz — from just a title and chapter.
          </p>

          <div className="mt-5" data-testid="hero-standards-badges">
            <StandardsBadges />
          </div>

          <div className="mt-4 inline-flex items-center gap-2.5 rounded-full border border-border bg-secondary/40 pl-2 pr-4 py-1.5" data-testid="hero-cornell-badge" title="The Cornell Note-Taking System was developed by Prof. Walter Pauk at Cornell University in the 1940s and is still taught in classrooms today.">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-[10px] font-black tracking-wider">
              CORNELL<span className="opacity-70 font-medium">·</span>METHOD
            </span>
            <span className="text-xs font-medium text-foreground/80">
              Cornell Note-Style study guides included
            </span>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-5">
              {scope !== "upload" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Book Title
                </label>
                <input
                  data-testid="input-book-title"
                  value={book}
                  onChange={(e) => setBook(e.target.value)}
                  placeholder="e.g. Sapiens: A Brief History of Humankind"
                  className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition"
                />
              </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  What should we cover?
                </label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <button
                    type="button"
                    data-testid="scope-chapter"
                    onClick={() => setScope("chapter")}
                    className={`rounded-xl border px-2 py-2.5 text-xs sm:text-sm font-semibold transition-all ${
                      scope === "chapter"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    A chapter
                  </button>
                  <button
                    type="button"
                    data-testid="scope-book"
                    onClick={() => setScope("book")}
                    className={`rounded-xl border px-2 py-2.5 text-xs sm:text-sm font-semibold transition-all ${
                      scope === "book"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    Whole book
                  </button>
                  <button
                    type="button"
                    data-testid="scope-paste"
                    onClick={() => setScope("paste")}
                    className={`rounded-xl border px-2 py-2.5 text-xs sm:text-sm font-semibold transition-all inline-flex items-center justify-center gap-1 ${
                      scope === "paste"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                    title="Paste the actual chapter text — Lectura builds everything from that exact source, not from memory."
                  >
                    <FileText className="h-3.5 w-3.5" /> Paste text
                  </button>
                  <button
                    type="button"
                    data-testid="scope-upload"
                    onClick={() => setScope("upload")}
                    className={`rounded-xl border px-2 py-2.5 text-xs sm:text-sm font-semibold transition-all inline-flex items-center justify-center gap-1 ${
                      scope === "upload"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                    title="Upload one or many chapter files (PDF, DOCX, image) — each becomes its own lecture."
                  >
                    <UploadCloud className="h-3.5 w-3.5" /> Upload files
                  </button>
                  <button
                    type="button"
                    data-testid="scope-youtube"
                    onClick={() => setScope("youtube")}
                    className={`rounded-xl border px-2 py-2.5 text-xs sm:text-sm font-semibold transition-all inline-flex items-center justify-center gap-1 col-span-2 sm:col-span-1 ${
                      scope === "youtube"
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary/50"
                    }`}
                    title="Turn any YouTube video with captions into a full lecture, slides, study guide + quiz."
                  >
                    <Youtube className="h-3.5 w-3.5" /> YouTube
                  </button>
                </div>
                {scope === "paste" && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Highest fidelity — Lectura writes the lecture, slides, guide & quiz straight from the text you paste, not from memory.
                  </p>
                )}
                {scope === "upload" && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Drop PDFs, Word docs or page images — Lectura reads each file and builds a full lecture for every one, filed under your chosen class → chapter.
                  </p>
                )}
                {scope === "youtube" && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Paste a YouTube URL — Lectura pulls the captions and builds a full lecture, slides, study guide & quiz from what was actually said in the video.
                  </p>
                )}
              </div>

              {needsChapter && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {chapterMode === "number" ? "Chapter Number(s)" : "Chapter Name"}
                  </label>
                  <div className="flex rounded-full border border-border p-0.5">
                    <button
                      type="button"
                      data-testid="chapter-mode-number"
                      onClick={() => { setChapterMode("number"); setChapters(""); }}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${chapterMode === "number" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      By number
                    </button>
                    <button
                      type="button"
                      data-testid="chapter-mode-name"
                      onClick={() => { setChapterMode("name"); setChapters(""); }}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${chapterMode === "name" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      By name
                    </button>
                  </div>
                </div>
                <input
                  data-testid="input-chapters"
                  value={chapters}
                  onChange={(e) => setChapters(e.target.value)}
                  placeholder={chapterMode === "number" ? "e.g. Chapter 6, or 3-5" : "e.g. A Tour of the Cell"}
                  className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition"
                />
              </div>
              )}

              {isYoutube && (
                <div className="space-y-3" data-testid="youtube-panel">
                  {/* URL / Search mode toggle */}
                  <div className="inline-flex rounded-full border border-border bg-secondary/40 p-1" data-testid="youtube-mode-toggle">
                    <button
                      type="button"
                      data-testid="youtube-mode-url"
                      onClick={() => setYtMode("url")}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                        ytMode === "url"
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <LinkIcon className="h-3.5 w-3.5" /> Paste URL
                    </button>
                    <button
                      type="button"
                      data-testid="youtube-mode-search"
                      onClick={() => setYtMode("search")}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                        ytMode === "search"
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Search className="h-3.5 w-3.5" /> Search by name
                    </button>
                  </div>

                  {ytMode === "url" && (
                    <>
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                        <Youtube className="h-3.5 w-3.5" /> YouTube URL
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <LinkIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <input
                            data-testid="input-youtube-url"
                            type="url"
                            value={ytUrl}
                            onChange={(e) => { setYtUrl(e.target.value); if (ytResult) setYtResult(null); }}
                            placeholder="https://www.youtube.com/watch?v=…"
                            className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition"
                          />
                        </div>
                        <button
                          type="button"
                          data-testid="fetch-youtube-btn"
                          onClick={() => fetchYoutube()}
                          disabled={!ytUrl.trim() || ytFetching}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 font-semibold disabled:opacity-60 hover:opacity-90 transition active:scale-95"
                        >
                          {ytFetching ? <><Loader2 className="h-4 w-4 animate-spin" /> Fetching…</> : "Pull captions"}
                        </button>
                      </div>
                      {ytFetching ? (
                        <p className="text-xs text-accent animate-pulse" data-testid="yt-fetching-hint">
                          Pulling captions from YouTube — this can take 10-20 seconds for longer videos. Don't refresh.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Works with any YouTube video that has captions (auto or human). Nothing gets downloaded from YouTube; we just read the captions Google already publishes.
                        </p>
                      )}
                    </>
                  )}

                  {ytMode === "search" && (
                    <>
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                        <Search className="h-3.5 w-3.5" /> Search YouTube
                      </label>
                      <form onSubmit={runYoutubeSearch} className="flex gap-2">
                        <div className="flex-1 relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <input
                            data-testid="input-youtube-query"
                            type="text"
                            value={ytQuery}
                            onChange={(e) => setYtQuery(e.target.value)}
                            placeholder="e.g. photosynthesis crash course, Krebs cycle, Feynman entropy"
                            className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition"
                          />
                        </div>
                        <button
                          type="submit"
                          data-testid="youtube-search-btn"
                          disabled={ytQuery.trim().length < 2 || ytSearching}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 font-semibold disabled:opacity-60 hover:opacity-90 transition"
                        >
                          {ytSearching ? <><Loader2 className="h-4 w-4 animate-spin" /> Searching…</> : "Search"}
                        </button>
                      </form>
                      <p className="text-xs text-muted-foreground">
                        Type the video title (or a topic). Pick any result — Lectura fetches the captions and turns them into your lecture.
                      </p>

                      {ytSearchResults !== null && (
                        <div data-testid="youtube-search-results" className="mt-1">
                          {ytSearchResults.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No videos matched. Try different keywords.</p>
                          ) : (
                            <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden bg-background max-h-[420px] overflow-y-auto">
                              {ytSearchResults.map((r) => (
                                <li
                                  key={r.video_id}
                                  data-testid={`youtube-result-${r.video_id}`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => pickSearchResult(r)}
                                    disabled={ytFetching}
                                    className="w-full flex gap-3 p-3 text-left hover:bg-secondary/40 transition disabled:opacity-60"
                                  >
                                    <div className="relative shrink-0">
                                      <img
                                        src={r.thumbnail}
                                        alt=""
                                        className="h-20 w-32 rounded-lg object-cover bg-secondary"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                      />
                                      {r.duration_text && (
                                        <span className="absolute bottom-1 right-1 rounded bg-black/80 text-white text-[10px] font-semibold px-1.5 py-0.5">
                                          {r.duration_text}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold line-clamp-2">{r.title}</p>
                                      <p className="mt-0.5 text-xs text-muted-foreground truncate">
                                        {r.channel}
                                      </p>
                                      <p className="mt-0.5 text-[11px] text-muted-foreground truncate">
                                        {[r.views_text, r.published_text].filter(Boolean).join(" · ")}
                                      </p>
                                    </div>
                                    <span className="self-center inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 shrink-0">
                                      Use this
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {ytResult && (
                    <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-3 flex gap-3" data-testid="youtube-preview">
                      <img
                        src={ytResult.thumbnail}
                        alt=""
                        className="h-20 w-32 rounded-lg object-cover shrink-0"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Transcript ready
                        </p>
                        <p className="mt-1 text-sm font-semibold truncate">Video {ytResult.video_id}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {ytResult.chars.toLocaleString()} chars · {Math.round((ytResult.duration_seconds || 0) / 60)} min · {ytResult.language}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2 italic">
                          "{ytResult.text.slice(0, 200)}…"
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {needsPastedText && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Paste your chapter text
                  </label>
                  <textarea
                    data-testid="input-source-text"
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value.slice(0, SOURCE_TEXT_MAX_CHARS))}
                    placeholder="Paste the exact chapter, article or reading material here (up to ~80,000 characters). Lectura will build every slide, quiz question and citation directly from THIS text — highest fidelity."
                    rows={10}
                    className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition font-mono resize-y min-h-[220px]"
                  />
                  <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                    <span>{sourceText.length < 400 && sourceText.length > 0 ? "Paste at least 400 characters to generate cleanly" : "Min 400 chars"}</span>
                    <span className={sourceText.length >= SOURCE_TEXT_MAX_CHARS ? "text-amber-600 font-semibold" : ""}>
                      {sourceText.length.toLocaleString()} / {SOURCE_TEXT_MAX_CHARS.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {scope === "upload" && (
                <div className="space-y-3" data-testid="upload-zone-block">
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                    <UploadCloud className="h-3.5 w-3.5" /> Upload chapter file(s) — up to 10 at once
                  </label>
                  <div
                    ref={dropZoneRef}
                    data-testid="upload-drop-zone"
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    className="rounded-2xl border-2 border-dashed border-border hover:border-accent/60 hover:bg-accent/5 transition cursor-pointer px-6 py-8 text-center"
                  >
                    <UploadCloud className="mx-auto h-8 w-8 text-accent" />
                    <p className="mt-3 font-semibold text-sm">Drop files here, or click to browse</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      PDF · DOCX · PNG/JPG (OCR) · TXT — max 25&nbsp;MB each
                    </p>
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                      <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400" data-testid="upload-av-badge">
                        <ShieldCheck className="h-3 w-3" /> Every upload is virus-scanned
                      </p>
                      {uploadLimits && (
                        <p
                          data-testid="upload-limit-counter"
                          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest ${
                            uploadLimits.remaining <= 3
                              ? "text-destructive"
                              : "text-muted-foreground"
                          }`}
                          title={`${uploadLimits.used} of ${uploadLimits.limit} uploads this hour`}
                        >
                          · {uploadLimits.remaining} of {uploadLimits.limit} left this hour
                        </p>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      data-testid="upload-file-input"
                      accept={ACCEPTED}
                      multiple
                      className="hidden"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    />
                  </div>

                  {uploads.length > 0 && (
                    <ul className="space-y-2" data-testid="upload-file-list">
                      {uploads.map((u) => (
                        <li key={u.id} data-testid={`upload-item-${u.id}`} className="rounded-xl border border-border bg-background/60 p-3 flex items-start gap-3">
                          <div className={`grid place-items-center h-8 w-8 rounded-lg shrink-0 ${
                            u.status === "ready" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                              : u.status === "error" ? "bg-destructive/10 text-destructive"
                              : "bg-secondary text-muted-foreground"
                          }`}>
                            {u.status === "extracting" ? <Loader2 className="h-4 w-4 animate-spin" />
                              : u.status === "ready" ? <CheckCircle2 className="h-4 w-4" />
                              : u.status === "error" ? <X className="h-4 w-4" />
                              : <FileType2 className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <p className="text-sm font-semibold truncate" title={u.filename}>{u.filename}</p>
                              {u.status === "ready" && u.chars > 0 && (
                                <span className="text-[10px] text-muted-foreground shrink-0">{u.chars.toLocaleString()} chars{u.truncated ? " · trimmed" : ""}</span>
                              )}
                            </div>
                            {u.status === "extracting" && <p className="mt-0.5 text-xs text-muted-foreground">Reading file…</p>}
                            {u.status === "error" && <p className="mt-0.5 text-xs text-destructive">{u.error}</p>}
                            {u.status === "ready" && (
                              <input
                                data-testid={`upload-title-${u.id}`}
                                value={u.titleOverride || ""}
                                onChange={(e) => setUploads((arr) => arr.map((x) => x.id === u.id ? { ...x, titleOverride: e.target.value } : x))}
                                placeholder="Lecture title"
                                className="mt-1.5 w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-ring/60 transition"
                              />
                            )}
                          </div>
                          <button
                            type="button"
                            data-testid={`upload-remove-${u.id}`}
                            onClick={() => removeUpload(u.id)}
                            className="shrink-0 grid place-items-center h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                            aria-label="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Target Level
                </label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {LEVELS.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      data-testid={`level-${l.value.replace(/\s+/g, "-")}`}
                      onClick={() => setLevel(l.value)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium transition-all ${
                        level === l.value
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Edition <span className="text-muted-foreground/60 normal-case tracking-normal font-normal">(optional — leave blank for the newest)</span>
                </label>
                <input
                  data-testid="input-edition"
                  value={edition}
                  onChange={(e) => setEdition(e.target.value)}
                  placeholder="e.g. 4th Edition, Norton Critical Ed., 10th Anniversary"
                  className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" /> Study Depth
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {depths.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      data-testid={`depth-${d.id}`}
                      onClick={() => changeDepth(d.id)}
                      className={`relative rounded-2xl border-2 p-3 text-left transition ${
                        depth === d.id ? "border-accent bg-accent/10" : "border-border hover:border-primary/40"
                      }`}
                    >
                      {d.id === "certification" && (
                        <span className="absolute -top-2 right-2 rounded-full bg-accent text-accent-foreground text-[9px] font-bold px-2 py-0.5">FULL EXAM</span>
                      )}
                      <div className="flex items-center gap-1.5 text-sm font-semibold">
                        <GraduationCap className="h-3.5 w-3.5" /> {d.label}
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground leading-snug">{d.blurb}</div>
                      <div className="mt-2 text-[11px] font-mono text-muted-foreground/80">
                        {d.slides} slides · {d.quiz} Qs · {d.time}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" /> Language
                </label>
                <select
                  data-testid="language-select"
                  value={language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-ring/60 transition cursor-pointer"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                {language !== "English" && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Your lecture, slides, study guide & quiz will be written in {language}.
                  </p>
                )}
              </div>

              <label className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/30 p-4 cursor-pointer hover:border-accent/40 transition">
                <input
                  data-testid="include-images-toggle"
                  type="checkbox"
                  checked={includeImages}
                  onChange={(e) => setIncludeImages(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-accent shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-accent" />
                    <span className="text-sm font-semibold">Auto-illustrate every slide</span>
                    <span className="rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">New</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Adds an AI-generated textbook illustration to every slide (Gemini Nano Banana). Slides feel like a real deck, not a text card. <span className="font-semibold">Adds ~30 s per slide</span> to generation time.
                  </p>
                </div>
              </label>

              <ContentVolumePicker
                components={components}
                onToggle={toggleComponent}
                user={user}
              />

              {isInstructor && (
                <div className="rounded-2xl border border-accent/40 bg-accent/5 p-4 space-y-3" data-testid="gen-classroom-block">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                    <School className="h-4 w-4" /> Also file into a class library <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal">Instructors only</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 -mt-1 text-[11px] leading-snug" data-testid="gen-classroom-explainer">
                    <div className="rounded-lg bg-background/70 border border-border px-3 py-2">
                      <p className="font-semibold text-foreground">📚 My Library <span className="text-muted-foreground font-normal">(always)</span></p>
                      <p className="text-muted-foreground mt-0.5">Your personal saved lectures — only you see them.</p>
                    </div>
                    <div className="rounded-lg bg-background/70 border border-accent/40 px-3 py-2">
                      <p className="font-semibold text-foreground">🎓 Class Library <span className="text-muted-foreground font-normal">(optional, below)</span></p>
                      <p className="text-muted-foreground mt-0.5">Files this under a chapter in a class you teach. Students still see nothing until you post an assignment.</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Class</label>
                    <select
                      data-testid="gen-classroom-select"
                      value={classroomId}
                      onChange={(e) => setClassroomId(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition cursor-pointer"
                    >
                      <option value="">— Skip · save to My Library only —</option>
                      {myClasses.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}{c.subject ? ` · ${c.subject}` : ""}</option>
                      ))}
                    </select>
                    {myClasses.length === 0 && (
                      <p className="mt-1.5 text-[11px] text-muted-foreground">You don't have any classes yet — create one on the Classes tab first.</p>
                    )}
                  </div>
                  {classroomId && (
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Chapter label</label>
                      <input
                        data-testid="gen-chapter-label"
                        value={chapterLabel}
                        onChange={(e) => setChapterLabel(e.target.value)}
                        placeholder="e.g. Chapter 6"
                        className="mt-1 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition"
                      />
                      <p className="mt-1 text-[11px] text-muted-foreground">Groups this lecture under a chapter in the class library.</p>
                    </div>
                  )}
                </div>
              )}

              {scope === "upload" ? (
                <button
                  type="button"
                  data-testid="btn-generate-upload-batch"
                  onClick={submitUploads}
                  disabled={batchGenerating || uploads.filter((u) => u.status === "ready").length === 0}
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold text-base disabled:opacity-50 hover:opacity-90 transition-opacity"
                >
                  {batchGenerating ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Starting your lectures…</>
                  ) : (
                    <><Wand2 className="h-5 w-5" /> Generate {uploads.filter((u) => u.status === "ready").length || ""} {uploads.filter((u) => u.status === "ready").length === 1 ? "Lecture" : "Lectures"}</>
                  )}
                </button>
              ) : (
                <button
                  data-testid="btn-generate-lecture"
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold text-base disabled:opacity-50 hover:opacity-90 transition-opacity active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Synthesizing your lecture…
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-5 w-5" /> {scope === "book" ? "Generate Book Overview" : scope === "paste" ? "Generate from Pasted Text" : "Generate Lecture"}
                    </>
                  )}
                </button>
              )}
              {loading && (
                <div className="text-center space-y-1" data-testid="gen-progress">
                  <p className="text-sm font-medium text-foreground/90 animate-pulse">
                    {progress?.message || "Preparing your study package…"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Deep & Certification runs take a few minutes — you can keep this tab open, we'll ping you when it's ready.
                  </p>
                </div>
              )}
              {/* Turns a dead disabled button into a "here's what to fix" nudge —
                  eliminates the "3 rapid clicks on Create" rage pattern. */}
              {!loading && !canSubmit && disabledReason && (
                <p className="mt-2 text-center text-xs font-medium text-accent" data-testid="gen-disabled-reason">
                  ↑ {disabledReason}
                </p>
              )}
            </div>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-border shadow-xl">
            <img src={HERO} alt="Study desk" className="w-full h-[380px] lg:h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-background/80 border border-border/60 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-accent">
                <Sparkles className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">AI Powered · Gemini</span>
              </div>
              <p className="mt-2 font-serif text-xl leading-snug">
                "The finest study companion — a professor in your pocket."
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
